import Popup from "../../lib/Popup";

import * as PIXI from "pixi.js";
import gsap from "gsap";
import { IGameSnacks } from "../../lib/GameSnacks";
import ServiceLocator from "../../ServiceLocator";
import Button from "../../lib/Button";
import { Profile } from "../../lib/game_snacks/IUser";
import Session from "../../Session";
import { LiveEventData, LiveEventDataType, LivePlayerDataEvent, LivePresenceAction, LiveRoomData } from "../../lib/game_snacks/ILiveMultiplayer";
import { Signal } from "signals";
import PRNG from "../../lib/PRNG";
import PlayerSlot from "./PlayerSlot";
import Settings from "../Settings";
import TutorialPopup from "./TutorialPopup";
import Utils from "../../Utils";
import MatchmakingReadyAnim from "./MatchmakingReadyAnim";

declare global {
    interface Window { GameSnacks: IGameSnacks; }
}


export default class MathcMakingPopup extends Popup
{
    // #region Attributes //

    // private:

    private _matchmakingReadyAnim: MatchmakingReadyAnim = null;
    private _headerText: PIXI.Text = null;
    private _magnifyingGlass: PIXI.Sprite = null;
    private _arrPlayerSlot: Array<PlayerSlot> = null;
    private _settings: Settings = null;
    private _backBtn: Button = null;

    private _state: state_id = null;
    private _autoStartTimer: number = null;
    private _addBotTimer: number = null;

    private _magnifyingGlassTween: gsap.core.Tween = null;

    private _onGameStarted: Signal = null;

    // #endregion //


    public get onGameStarted(): Signal { return this._onGameStarted; } 


    // #region Methods //

    // public:

    public onEnter(): void
    {
        super.onEnter();        

        this._onGameStarted = new Signal();

        this._state = state_id.IDLE;

        this._arrPlayerSlot = [];

        let bg: PIXI.Sprite = new PIXI.Sprite( ServiceLocator.resources.bg.texture );
        bg.anchor.set( 0.5 );
        bg.position.set( ServiceLocator.app.width * 0.5, ServiceLocator.app.height * 0.5 );
        this._root.addChild( bg );

        this._matchmakingReadyAnim = new MatchmakingReadyAnim();
        this._matchmakingReadyAnim.playerSlots = this._arrPlayerSlot;
        this._matchmakingReadyAnim.init();
        this._root.addChild( this._matchmakingReadyAnim );
        this._matchmakingReadyAnim.visible = false;

        this._headerText = new PIXI.Text( "Looking for players...", { fontFamily: "asap_bold", fontSize: 50, fill: 0xffffdf } );
        this._headerText.anchor.set( 0.5 );
        this._headerText.position.set( ServiceLocator.app.width * 0.5, 218 );
        this._root.addChild( this._headerText );

        const kArrPos: Array<PIXI.Point> = [ 
            new PIXI.Point( ServiceLocator.app.width * 0.5, 764 ),
            new PIXI.Point( 105, 550 ),
            new PIXI.Point( 526, 550 ),
            new PIXI.Point( ServiceLocator.app.width * 0.5, 354 ) ];
        for ( let i: number = 0; i < Session.i.kGameRoomSize; ++i )
        {
            let playerSlot: PlayerSlot = new PlayerSlot();
            playerSlot.position.copyFrom( kArrPos[ i ] );
            this._root.addChild( playerSlot );            
            //
            this._arrPlayerSlot.push( playerSlot );
        }
        this._arrPlayerSlot[ 0 ].setPlayer( Session.i.profile );
        this._arrPlayerSlot[ 0 ].scale.set( 1.2 );

        this._backBtn = new Button( { on: "button_back", pressed: "button_back_pressed" } );
        this._backBtn.anchor.set( 0.5 );
        this._backBtn.position.set( 58 );
        this._root.addChild( this._backBtn );
        this._backBtn.on( "pointertap", () => { 
            this._root.interactiveChildren = false;
            if ( window.GameSnacks )
            {
                window.GameSnacks.liveMultiplayer.updatePlayerPresence( LivePresenceAction.LEAVE )
                    .then( () => {
                        this._onClosed.dispatch( this ); 
                    } );
            }
            else
            {
                this._onClosed.dispatch( this ); 
            }
        } );

        this._magnifyingGlass = new PIXI.Sprite( ServiceLocator.resources.magnifying_glass.texture );
        this._magnifyingGlass.anchor.set( 70/173, 68/206 );
        this._magnifyingGlass.position.set( ServiceLocator.app.width * 0.5 - 40, 577 );
        this._root.addChild( this._magnifyingGlass );
        //
        this._magnifyingGlassTween = gsap.to( {}, { duration: 2, repeat: -1, onUpdate: () => {
          let m: PIXI.Matrix = PIXI.Matrix.IDENTITY.clone();
          m.tx = -40;
          m.rotate( this._magnifyingGlassTween.progress() * Math.PI * 2 );
          
          this._magnifyingGlass.position.set( ServiceLocator.app.width * 0.5 + m.tx, 577 + m.ty );
        } } );

        this._settings = new Settings();
        this._settings.init( false );
        this._settings.position.set( ServiceLocator.app.width - 58, 58 );
        this._root.addChild( this._settings );
        this._settings.onTutorial.add( () => { 
            let tutorialPopup: TutorialPopup = new TutorialPopup();
            tutorialPopup.init();
            this._root.addChild( tutorialPopup );
            tutorialPopup.onClosed.add( ( popup: TutorialPopup ) => {
                popup.end();
            } );
        } );
        //
        const kSettingsScale: number = 100 / this._settings.width;
        this._settings.position.x -= 58 * ( kSettingsScale - 1 ); 
        this._settings.position.y += 58 * ( kSettingsScale - 1 ) - 4; 
        this._settings.scale.set( kSettingsScale );

        ServiceLocator.multiplayerEventManager.onPlayerPresenceUpdated.add( this._onMultiplayer_PlayerPresenceUpdated, this );
        ServiceLocator.multiplayerEventManager.onRoomLoaded.add( this._onRoom_Loaded, this );
        ServiceLocator.multiplayerEventManager.onEventsReceived.add( this._onMultiplayer_EventsReceived, this );
        ServiceLocator.multiplayerEventManager.onProfileChange.add( this._onProfile_Changed, this );

        window.GameSnacks.liveMultiplayer.updatePlayerPresence( LivePresenceAction.JOIN );

        Session.i.room = [];
    }

        private _onMultiplayer_PlayerPresenceUpdated( presenceEventPromise: Promise<LivePlayerDataEvent> ): void
        {
            presenceEventPromise
                .then( ( livePlayerDataEvent: LivePlayerDataEvent ) => {
                    this._autoStartTimer = 0;

                    if ( livePlayerDataEvent.action == LivePresenceAction.JOIN )
                    {
                        let profile: Profile = livePlayerDataEvent.profile;
                        profile.uid = livePlayerDataEvent.uid;
                        Session.i.room.push( profile );

                        if ( livePlayerDataEvent.isLocalPlayer )
                        {
                            Session.i.profile.uid = profile.uid;

                            this._arrPlayerSlot[ 0 ].setPlayer( profile );

                            this._updateOtherPlayerSlots();
                        }
                        else
                        {
                            this._updateOtherPlayerSlots();

                            if ( Session.i.room[ 0 ].uid == Session.i.profile.uid
                                && Session.i.room.length == Session.i.kGameRoomSize )
                            {
                                this._autoStartTimer = 11;
                            }
                        }
                    }
                    else if ( livePlayerDataEvent.action == LivePresenceAction.LEAVE )
                    {
                        if ( Session.i.profile.uid != livePlayerDataEvent.uid ) 
                        {
                            for ( let i: number = 0; i < Session.i.room.length; ++i )
                            {
                                if ( Session.i.room[ i ].uid == livePlayerDataEvent.uid )
                                {
                                    if ( Session.i.isGameStarted )
                                    {
                                        Session.i.room[ i ].isBot = true;
                                    }
                                    else
                                    {
                                        Session.i.room.splice( i, 1 );
                                    }
                                    break;
                                }
                            }

                            if ( !Session.i.isGameStarted )
                            {
                                this._updateOtherPlayerSlots();
                            }
                        }
                    }
                } );
        }

            private _updateOtherPlayerSlots(): void
            {
                for ( let i: number = 1; i < Session.i.kGameRoomSize; ++i )
                {
                    this._arrPlayerSlot[ i ].empty();
                }

                let myProfileIndex: number = 0;
                while ( Session.i.room[ myProfileIndex ].uid != Session.i.profile.uid )
                {
                    myProfileIndex += 1;
                }
                
                switch ( myProfileIndex )
                {
                    case 0:
                        {
                            if ( Session.i.room.length == 2 )
                            {
                                this._arrPlayerSlot[ 1 ].setPlayer( Session.i.room[ 1 ] );
                            }
                            else if ( Session.i.room.length == 3 )
                            {
                                this._arrPlayerSlot[ 1 ].setPlayer( Session.i.room[ 1 ] );
                                this._arrPlayerSlot[ 2 ].setPlayer( Session.i.room[ 2 ] );
                            }
                            else if ( Session.i.room.length == 4 )
                            {
                                this._arrPlayerSlot[ 1 ].setPlayer( Session.i.room[ 1 ] );
                                this._arrPlayerSlot[ 2 ].setPlayer( Session.i.room[ 2 ] );
                                this._arrPlayerSlot[ 3 ].setPlayer( Session.i.room[ 3 ] );
                            }
                            break;
                        }

                    case 1:
                        {
                            if ( Session.i.room.length == 2 )
                            {
                                this._arrPlayerSlot[ 1 ].setPlayer( Session.i.room[ 0 ] );
                            }
                            else if ( Session.i.room.length == 3 )
                            {
                                this._arrPlayerSlot[ 1 ].setPlayer( Session.i.room[ 2 ] );
                                this._arrPlayerSlot[ 2 ].setPlayer( Session.i.room[ 0 ] );
                            }
                            else if ( Session.i.room.length == 4 )
                            {
                                this._arrPlayerSlot[ 1 ].setPlayer( Session.i.room[ 3 ] );
                                this._arrPlayerSlot[ 2 ].setPlayer( Session.i.room[ 0 ] );
                                this._arrPlayerSlot[ 3 ].setPlayer( Session.i.room[ 2 ] );
                            }
                            break;
                        }

                    case 2:
                        {
                            if ( Session.i.room.length < 4 )
                            {
                                this._arrPlayerSlot[ 1 ].setPlayer( Session.i.room[ 0 ] );
                                this._arrPlayerSlot[ 2 ].setPlayer( Session.i.room[ 1 ] );
                            }
                            else
                            {
                                this._arrPlayerSlot[ 1 ].setPlayer( Session.i.room[ 0 ] );
                                this._arrPlayerSlot[ 2 ].setPlayer( Session.i.room[ 3 ] );
                                this._arrPlayerSlot[ 3 ].setPlayer( Session.i.room[ 1 ] );
                            }
                            break;
                        }

                    case 3:
                        {
                            this._arrPlayerSlot[ 1 ].setPlayer( Session.i.room[ 2 ] );
                            this._arrPlayerSlot[ 2 ].setPlayer( Session.i.room[ 1 ] );
                            this._arrPlayerSlot[ 3 ].setPlayer( Session.i.room[ 0 ] );
                            break;
                        }
                }
            }

        private _onRoom_Loaded( liveRoomData: Promise<LiveRoomData> ): void
        {
            liveRoomData
                .then( ( liveRoomData: LiveRoomData ) => {
                    Session.i.room = [];
                    for ( let i: number = 0; i < liveRoomData.players.length; ++i )
                    {
                        if ( liveRoomData.players[ i ].action == LivePresenceAction.JOIN )
                        {
                            let profile: Profile = liveRoomData.players[ i ].profile;
                            profile.uid = liveRoomData.players[ i ].uid;
                            Session.i.room.push( profile );
                        }
                    }
                    this._updateOtherPlayerSlots();
                } );
        }

        private _onMultiplayer_EventsReceived( eventsPromise: Promise<LiveEventData[]> ): void
        {
            eventsPromise
                .then( ( arrData: LiveEventData[] ) => {
                    while ( arrData.length > 0 )
                    {
                        let data: LiveEventData = arrData.shift();
                        if ( data.type === LiveEventDataType.START_GAME
                            && !Session.i.isGameStarted )
                        {
                            let kStartGamePayload: IStartGamePayload = JSON.parse( data.payload );
                            Session.i.prng = new PRNG( kStartGamePayload.seed );
                            //
                            Session.i.room = kStartGamePayload.room;

                            this._updateOtherPlayerSlots();
                            //
                            for ( let playerSlot of this._arrPlayerSlot )
                            {
                                if ( playerSlot.profile.isBot )
                                {
                                    playerSlot.empty( playerSlot.profile );
                                    if ( this._state == state_id.IDLE )
                                    {
                                        this._state = state_id.ADDING_BOTS;

                                        this._addBotTimer = 0;
                                    }
                                }
                            }
                            
                            let arrProfile: Array<Profile> = [];
                            arrProfile.push( Session.i.room[ 0 ] );
                            arrProfile.push( Session.i.room[ 1 ] );
                            if ( Session.i.room.length == 4 )
                            {
                                arrProfile.push( Session.i.room[ 3 ] );
                            }
                            arrProfile.push( Session.i.room[ 2 ] );
                            //
                            Session.i.room = arrProfile;

                            Session.i.isGameStarted = true;

                            if ( this._state == state_id.IDLE )
                            {
                                this._playGameReadyAnim();
                            }
                        }
                        else
                        {
                            Session.i.gameEvents.push( data );
                        }
                    }
                } );
        }

            private _playGameReadyAnim(): void
            {
                if ( this._magnifyingGlassTween )
                {
                    this._magnifyingGlassTween.kill();
                    this._magnifyingGlassTween = null;
                }
                this._magnifyingGlass.visible = false;
                //
                this._headerText.visible = false;
                this._backBtn.visible = false;
                this._settings.visible = false;
                
                this._matchmakingReadyAnim.visible = true;
                let tl: gsap.core.Timeline = this._matchmakingReadyAnim.play();
                tl.call( () => { this._onGameStarted.dispatch( this ); } );
            }

            private _fillRoomWithBots(): void
            {                
                const kBotCount: number = Session.i.kGameRoomSize - Session.i.room.length;
                const kArrBotName: Array<string> = Utils.generateBotNames( kBotCount );
                
                let arrBotAvatarIndex: Array<number> = [];
                while ( arrBotAvatarIndex.length < kBotCount )
                {
                    const kBotAvatarIndex: number = Math.floor( Math.random() * 12 );
                    if ( !arrBotAvatarIndex.includes( kBotAvatarIndex ) )
                    {
                        arrBotAvatarIndex.push( kBotAvatarIndex );
                    }
                }

                let botCount: number = 0;
                while ( Session.i.room.length < Session.i.kGameRoomSize )
                {
                    let profile: Profile = { 
                        username: { prefix: kArrBotName[ botCount ], suffix: botCount.toString() },
                        avatar: { src: "avatar_" + ( arrBotAvatarIndex[ botCount ] ).toString(), alt: null } };
                    profile.isBot = true;
                    Session.i.room.push( profile );

                    botCount += 1;
                }  
            }

        private _onProfile_Changed( profile: Profile): void
        {
            Session.i.profile = profile;

            this._arrPlayerSlot[ 0 ].setPlayer( Session.i.profile )
        }

    public onLeave(): void
    {
        this._onGameStarted.dispose();
        this._onGameStarted = null;

        ServiceLocator.multiplayerEventManager.onPlayerPresenceUpdated.removeAll();
        ServiceLocator.multiplayerEventManager.onRoomLoaded.removeAll();
        ServiceLocator.multiplayerEventManager.onEventsReceived.removeAll();
        ServiceLocator.multiplayerEventManager.onProfileChange.remove( this._onProfile_Changed, this );

        Session.i.isGameStarted = false;

        if ( this._magnifyingGlassTween )
        {
            this._magnifyingGlassTween.kill();
            this._magnifyingGlassTween = null;
        }

        this._matchmakingReadyAnim.end();
        this._matchmakingReadyAnim = null;

        this._settings.end();
        this._settings = null;

        super.onLeave();
    }

    public onUpdate( dt: number ): void
    {
        if ( this._matchmakingReadyAnim.visible )
        {
            this._matchmakingReadyAnim.update( dt );
        }

        if ( this._state == state_id.IDLE )
        {
            if ( Session.i.isGameStarted ) { return; }

            if ( Session.i.room.length > 0
                //&& Session.i.profile.uid == Session.i.room[ 0 ].uid
                && Session.i.room.length <= Session.i.kGameRoomSize )
            {
                this._autoStartTimer += dt;
                if ( this._autoStartTimer > 10 ) 
                //if ( this._autoStartTimer > 0 ) // TEST: 
                {
                    this._fillRoomWithBots();

                    const kSeed: number = Math.floor( Math.random() * 9999 );
                    //
                    //ServiceLocator.multiplayerEventManager.onEventsReceived.removeAll();
                    //
                    window.GameSnacks.liveMultiplayer.sendEvent( { 
                        type: LiveEventDataType.START_GAME,
                        payload: JSON.stringify( { seed: kSeed, room: Session.i.room } )
                    } );

                    //Session.i.prng = new PRNG( kSeed );

                    //this._onGameStarted.dispatch( this, game_type.MATCHMAKING );
                }
            }
        }
        else if ( this._state == state_id.ADDING_BOTS )
        {
            this._addBotTimer -= dt;
            if ( this._addBotTimer < 0 )
            {
                for ( let playerSlot of this._arrPlayerSlot )
                {
                    if ( playerSlot.isEmpty )
                    {
                        playerSlot.setPlayer( playerSlot.profile );
                        break;
                    }
                }

                let isGameReady: boolean = true;
                for ( let playerSlot of this._arrPlayerSlot )
                {
                    if ( playerSlot.isEmpty )
                    {
                        isGameReady = false;
                        break;
                    }
                }
                if ( isGameReady )
                {
                    this._state = state_id.IDLE;

                    this._playGameReadyAnim();
                }
                else
                {
                    this._addBotTimer = Session.i.prng.randInterval( 1, 3 );
                    //this._addBotTimer = 0; // TEST:
                }
            }
        }
    }

    // #endregion //
}

export interface IStartGamePayload
{
    seed: number,
    room: Array<Profile>
}

const enum state_id
{
    IDLE = 0,
    ADDING_BOTS
}